/**
 * © 2025 Little Shilling, Inc.
 * Shon Little
 * Created: 2025-01-23
 */

import React, { useState, useEffect } from 'react';
import {
  Box,
  TextField,
  Typography,
  FormControlLabel,
  Switch,
  Alert,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Autocomplete,
  FormHelperText,
} from '@mui/material';

import { StudioFormFieldsProps } from '../../../types/studioForms';
import { LogoUpload } from './LogoUpload';
import {
  TIMEZONE_OPTIONS,
  STUDIO_TYPE_OPTIONS,
  TimezoneOption,
  StudioTypeOption,
} from '../../../utils/studioSettings';

export const StudioFormFields: React.FC<StudioFormFieldsProps> = ({
  formData,
  onChange,
  errors,
  disabled = false,
  studio,
}) => {
  const [logoPreview, setLogoPreview] = useState<string | null>(null);

  // Handle field changes
  const handleFieldChange = (field: string, value: any) => {
    if (field.startsWith('businessInfo.')) {
      const businessField = field.replace('businessInfo.', '');
      onChange('businessInfo', {
        ...formData.businessInfo,
        [businessField]: value,
      });
    } else {
      onChange(field as keyof typeof formData, value);
    }
  };

  // Handle logo file selection with preview generation
  const handleLogoChange = (file: File | null) => {
    // Clean up previous preview URL to prevent memory leaks
    if (logoPreview) {
      URL.revokeObjectURL(logoPreview);
      setLogoPreview(null);
    }

    // Generate new preview URL if file is selected
    if (file) {
      const previewUrl = URL.createObjectURL(file);
      setLogoPreview(previewUrl);
    }

    onChange('logo', file);
  };

  // Cleanup preview URL when component unmounts or logo changes
  useEffect(
    () => () => {
      if (logoPreview) {
        URL.revokeObjectURL(logoPreview);
      }
    },
    [logoPreview]
  );

  // Cleanup when formData.logo is cleared externally
  useEffect(() => {
    if (!formData.logo && logoPreview) {
      URL.revokeObjectURL(logoPreview);
      setLogoPreview(null);
    }
  }, [formData.logo, logoPreview]);

  return (
    <Box sx={{ width: '100%' }}>
      {/* Basic Information Section */}
      <Typography variant="h6" gutterBottom sx={{ mt: 2, mb: 3 }}>
        Basic Information
      </Typography>

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
          gap: 3,
        }}
      >
        {/* Studio Name */}
        <TextField
          fullWidth
          label="Studio Name"
          value={formData.name}
          onChange={e => handleFieldChange('name', e.target.value)}
          error={Boolean(errors.name)}
          helperText={errors.name}
          disabled={disabled}
          required
          placeholder="Enter your studio name"
        />

        {/* Description */}
        <Box sx={{ gridColumn: '1 / -1' }}>
          <TextField
            fullWidth
            label="Description"
            value={formData.description}
            onChange={e => handleFieldChange('description', e.target.value)}
            error={Boolean(errors.description)}
            helperText={
              errors.description ||
              `${formData.description.length}/500 characters`
            }
            disabled={disabled}
            multiline
            rows={3}
            placeholder="Describe your studio, classes, and what makes you unique"
          />
        </Box>

        {/* Logo Upload */}
        <Box sx={{ gridColumn: '1 / -1' }}>
          <LogoUpload
            currentLogo={studio?.logo}
            onFileSelect={handleLogoChange}
            error={errors.logo}
            disabled={disabled}
            preview={logoPreview}
          />
        </Box>
      </Box>

      {/* Business Information Section */}
      <Typography variant="h6" gutterBottom sx={{ mt: 4, mb: 3 }}>
        Business Information
      </Typography>

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
          gap: 3,
        }}
      >
        {/* Address */}
        <Box sx={{ gridColumn: '1 / -1' }}>
          <TextField
            fullWidth
            label="Business Address"
            value={formData.businessInfo.address || ''}
            onChange={e =>
              handleFieldChange('businessInfo.address', e.target.value)
            }
            error={Boolean(errors.address)}
            helperText={errors.address}
            disabled={disabled}
            placeholder="Enter your studio's physical address"
          />
        </Box>

        {/* Phone */}
        <TextField
          fullWidth
          label="Phone Number"
          value={formData.businessInfo.phone || ''}
          onChange={e =>
            handleFieldChange('businessInfo.phone', e.target.value)
          }
          error={Boolean(errors.phone)}
          helperText={errors.phone}
          disabled={disabled}
          placeholder="(555) 123-4567"
        />

        {/* Website */}
        <TextField
          fullWidth
          label="Website"
          value={formData.businessInfo.website || ''}
          onChange={e =>
            handleFieldChange('businessInfo.website', e.target.value)
          }
          error={Boolean(errors.website)}
          helperText={errors.website}
          disabled={disabled}
          placeholder="https://yourstudio.com"
        />
      </Box>

      {/* Studio Settings Section */}
      <Typography variant="h6" gutterBottom sx={{ mt: 4, mb: 3 }}>
        Studio Settings
      </Typography>

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
          gap: 3,
        }}
      >
        {/* Studio Type */}
        <FormControl fullWidth error={Boolean(errors.studioType)}>
          <InputLabel id="studio-type-label">Studio Type</InputLabel>
          <Select
            labelId="studio-type-label"
            value={formData.studioType || ''}
            onChange={e => handleFieldChange('studioType', e.target.value)}
            label="Studio Type"
            disabled={disabled}
          >
            {STUDIO_TYPE_OPTIONS.map((option: StudioTypeOption) => (
              <MenuItem key={option.value} value={option.value}>
                <Box>
                  <Typography variant="body1">{option.label}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    {option.description}
                  </Typography>
                </Box>
              </MenuItem>
            ))}
          </Select>
          {errors.studioType && (
            <FormHelperText>{errors.studioType}</FormHelperText>
          )}
        </FormControl>

        {/* Timezone */}
        <Autocomplete
          options={TIMEZONE_OPTIONS}
          getOptionLabel={(option: TimezoneOption) => option.label}
          value={
            TIMEZONE_OPTIONS.find(
              (option: TimezoneOption) => option.value === formData.timezone
            ) || null
          }
          onChange={(_, newValue: TimezoneOption | null) =>
            handleFieldChange('timezone', newValue?.value || '')
          }
          disabled={disabled}
          renderInput={params => (
            <TextField
              // eslint-disable-next-line react/jsx-props-no-spreading
              {...params}
              label="Timezone"
              error={Boolean(errors.timezone)}
              helperText={errors.timezone}
              placeholder="Search for your timezone"
            />
          )}
          renderOption={(props, option: TimezoneOption) => {
            const { key, ...otherProps } = props;
            return (
              <Box
                component="li"
                key={key}
                // eslint-disable-next-line react/jsx-props-no-spreading
                {...otherProps}
              >
                <Typography variant="body2">{option.label}</Typography>
              </Box>
            );
          }}
        />
      </Box>

      {/* Discovery Settings Section */}
      <Typography variant="h6" gutterBottom sx={{ mt: 4, mb: 3 }}>
        Discovery Settings
      </Typography>

      <Box sx={{ mb: 3 }}>
        <FormControlLabel
          control={
            <Switch
              checked={formData.allowJoinRequests || false}
              onChange={e =>
                handleFieldChange('allowJoinRequests', e.target.checked)
              }
              disabled={disabled}
              color="primary"
            />
          }
          label="Make Studio Discoverable"
          sx={{ mb: 1 }}
        />
        <Typography variant="body2" color="text.secondary" sx={{ ml: 4 }}>
          When enabled, other users can find your studio and request to join
          through the &ldquo;Add Studio&rdquo; page. You can review and approve
          join requests in your studio&apos;s Members section.
        </Typography>

        {formData.allowJoinRequests && (
          <Box sx={{ mt: 2, ml: 4 }}>
            <Alert severity="info" sx={{ mb: 3 }}>
              Your studio is currently discoverable. Users can find and request
              to join your studio. All join requests require your approval.
            </Alert>

            <TextField
              fullWidth
              label="Join Request Message"
              value={formData.joinRequestMessage || ''}
              onChange={e =>
                handleFieldChange('joinRequestMessage', e.target.value)
              }
              error={Boolean(errors.joinRequestMessage)}
              helperText={
                errors.joinRequestMessage ||
                `${(formData.joinRequestMessage || '').length}/500 characters`
              }
              disabled={disabled}
              multiline
              rows={3}
              placeholder="Looking to join our studio team? Share your experience, specialties, and availability. Whether you're interested in teaching, admin work, or substitute coverage, we'd love to hear from you!"
              sx={{ mt: 1 }}
            />
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              This message will be shown to users when they request to join your
              studio. Leave empty to use the default welcome message.
            </Typography>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default StudioFormFields;
